<template lang="pug">
.move-room-schedule-list-tab
  .mb-3
  .d-flex.justify-content-start.align-items-center.mb-2
    .d-flex.justify-content-start
      .error-message {{ errorMessage }}
    .d-flex.justify-content-end.flex-fill
      button.btn(type="button" @click="onAdd") 追加
  MoveRoomScheduleTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    @onEdit="onEdit"
    @onDelete="showConfirmDeleteModal"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue, Prop } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import MoveRoomSchedulesService, { MoveRoomSchedulesGetCountParam, MoveRoomSchedulesGetModel, MoveRoomSchedulesGetsParam } from "@/commons/services/MoveRoomSchedules";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import RelativeLoading from "@/pc/basics/PageLoading.vue";
import MoveRoomScheduleTable, { MoveRoomScheduleTableField, MoveRoomScheduleTableItem } from "@/pc/containers/roomSchedules/MoveRoomScheduleTable.vue";

/**
 * ### 仮押さえ／転床一覧タブコンポーネント
 */
@Component({
  components: {
    RelativeLoading,
    OkCancelModal,
    OkModal,
    MoveRoomScheduleTable
  }
})
export default class MoveRoomScheduleListTab extends Vue {
  /** 転床サービス */
  @Inject("MoveRoomSchedulesService") moveRoomSchedulesService!: MoveRoomSchedulesService;

  @Prop()
  errorMessage!: string;

  /** 列名 */
  fields: Array<MoveRoomScheduleTableField> = [];
  /** 各行の情報 */
  items: Array<MoveRoomScheduleTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.moveRoomSchedule.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.moveRoomSchedule.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.moveRoomSchedule.pageRange;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `move-room-schedule-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1; // 削除による画面の再描画もあるので、現在ページ番号はここで初期化する

    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<MoveRoomScheduleTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.resident.perPage);

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<MoveRoomScheduleTableField> {
    let fields: Array<MoveRoomScheduleTableField> = [];

    fields.push({ key: "type", label: "種別", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "residentName", label: "利用者名", sortable: false, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "roomName", label: "部屋名", sortable: false, sortDirection: "desc", class: "room-name" });
    fields.push({ key: "startDatetime", label: "開始", sortable: false, sortDirection: "desc", class: "start-datetime" });
    fields.push({ key: "endDatetime", label: "終了", sortable: false, sortDirection: "desc", class: "end-datetime" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["move-room-schedule-list"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const moveRoomSchedules: Array<MoveRoomSchedulesGetModel> = await this.getMoveRoomSchedules();
    const items: Array<MoveRoomScheduleTableItem> = this.createItems(moveRoomSchedules);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isLoading = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   *
   * - 過去は表示対象外にするため、開始日時を指定する
   * - 開始日時は当月1日の00:00:00を指定する
   */
  public async setRows() {
    try {
      const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymm01000000000");
      const params: MoveRoomSchedulesGetCountParam = {
        start: `${start}`
      };

      const count: number = await this.moveRoomSchedulesService.getCount(userStore, userStore.loginUser.facilityId, params);
      this.rows = count;
    } catch (error) {
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 仮押さえ／転床情報を取得する
   *
   * - 過去は表示対象外にするため、開始日時を指定する
   * - ページングを考慮し、必要数分だけ取得する
   */
  public async getMoveRoomSchedules(): Promise<Array<MoveRoomSchedulesGetModel>> {
    const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymm01000000000");
    const startIndex: number = this.currentRows;
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: MoveRoomSchedulesGetsParam = {
      start: `${start}`,
      startIndex: `${startIndex}`,
      endIndex: `${endIndex}`
    };

    const responce: Array<MoveRoomSchedulesGetModel> = await this.moveRoomSchedulesService.gets(userStore, userStore.loginUser.facilityId, params);

    return responce;
  }

  /**
   * ### 行情報を作成する
   */
  public createItems(moveRoomSchedules: Array<MoveRoomSchedulesGetModel>): Array<MoveRoomScheduleTableItem> {
    let items: Array<MoveRoomScheduleTableItem> = [];

    for (const moveRoomSchedule of moveRoomSchedules) {
      items.push({
        ...moveRoomSchedule
      });
    }

    return items;
  }

  /**
   * ### 親コンポーネントに追加ボタン押下を通知する
   */
  public onAdd(): void {
    this.$emit("onAdd");
    return;
  }

  /**
   * ### 親コンポーネントに修正ボタン押下を通知する
   */
  public onEdit(moveRoomSchedule: MoveRoomScheduleTableItem): void {
    this.$emit("onEdit", moveRoomSchedule);
    return;
  }

  /**
   * ### 仮押さえ／転床情報削除を親コンポーネントに通知する
   */
  public showConfirmDeleteModal(moveRoomSchedule: MoveRoomScheduleTableItem): void {
    this.$emit("onDelete", moveRoomSchedule);

    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    this.items = [];
    this.currentRows = this.items.length;
    await this.createView();

    return;
  }

  /**
   * ### ページネーションのページ番号押下
   *
   * - 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }
}
</script>

<style lang="scss" scoped>
.move-room-schedule-list-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}
</style>
